import React from 'react'

import { CloudImageOptions } from './types'
import { useCloudImage } from './useCloudImage'

export type CloudImageElementProps = {
  imageId: string
  options?: CloudImageOptions
}

export type CloudImageProps = CloudImageElementProps &
  React.ImgHTMLAttributes<HTMLImageElement> & {
    htmlWidth?: number | string
    htmlHeight?: number | string
  }

/**
 * CloudImage component renders an optimized image from a cloud provider
 * (e.g., Cloudinary, Cloudflare).
 *
 * @param props - Component props including imageId, options, and standard img attributes
 * @param props.imageId - Unique identifier for the cloud-hosted image
 * @param props.options - Optional transformation and optimization options for the image
 * @returns An img element with optimized cloud-hosted image sources
 *
 * @example
 * ```tsx
 * <CloudImage
 *   imageId="my-image-id"
 *   options={{ width: 300, height: 200, format: 'webp' }}
 *   alt="Description"
 * />
 * ```
 */
export function CloudImage({
  imageId,
  options = {},
  ...props
}: CloudImageProps) {
  const { cloudImageUrl } = useCloudImage()

  if (!imageId) return null

  // use htmlWidth, htmlHeight from <Image /> component from `@masterplandev/ui` package
  const { htmlWidth, htmlHeight, ...imageProps } = props
  const src = cloudImageUrl(imageId, options)
  const srcSet = `${cloudImageUrl(imageId, { dpr: 2, ...options })} 2x`

  return (
    // eslint-disable-next-line jsx-a11y/alt-text
    <img
      width={htmlWidth}
      height={htmlHeight}
      {...imageProps}
      src={src}
      srcSet={srcSet}
    />
  )
}
